import React, { createContext, useContext, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useReadUser, useOrganizationRead } from "../services/api";
import { User as UserType } from "../common/types";
import { getRuntimeConfig, isRuntimeFlagEnabled } from "../config/runtimeConfig";
import PageLoading from "../components/PageLoading";
import PageError from "../components/PageError";

export interface OrgDetails {
  name: string;
  status: string;
  autoEnrollmentEnabled: boolean;
}

export interface OrgSettingsContextValue {
  user: UserType;
  organizationDetails: OrgDetails | null;
  loadingOrgDetails: boolean;
  isEnterpriseMode: boolean;
  isEmailAdminEnabled: boolean;
}

const OrgSettingsContext = createContext<OrgSettingsContextValue | null>(null);

export const useOrgSettingsContext = (): OrgSettingsContextValue => {
  const ctx = useContext(OrgSettingsContext);
  if (!ctx)
    throw new Error(
      "useOrgSettingsContext must be used within OrgSettingsProvider",
    );
  return ctx;
};

const OrgSettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const readUserAPI = useReadUser();
  const organizationReadAPI = useOrganizationRead();

  const runtimeConfig = getRuntimeConfig();
  const isEnterpriseMode = runtimeConfig.paymentMethod === "ENTERPRISE";
  const isEmailAdminEnabled =
    isRuntimeFlagEnabled(runtimeConfig.enableEmailAdmin) ||
    process.env.NODE_ENV === "development";

  const [user, setUser] = useState<UserType | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  const [organizationDetails, setOrganizationDetails] =
    useState<OrgDetails | null>(null);
  const [loadingOrgDetails, setLoadingOrgDetails] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await readUserAPI();
        setUser(userData);
      } catch (err: any) {
        setUserError(err.message || "Failed to fetch user data");
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.organization_id || user?.role_in_org !== "admin") return;

    const fetchOrgDetails = async () => {
      setLoadingOrgDetails(true);
      try {
        const orgData = await organizationReadAPI();
        if (orgData.organization) {
          setOrganizationDetails({
            name: orgData.organization.name,
            status: orgData.organization.status,
            autoEnrollmentEnabled:
              orgData.organization.auto_enrollment_enabled ?? false,
          });
        }
      } catch (err: any) {
        console.error("Failed to fetch org details:", err);
      } finally {
        setLoadingOrgDetails(false);
      }
    };

    fetchOrgDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organization_id, user?.role_in_org]);

  if (loadingUser) return <PageLoading />;
  if (userError) return <PageError>{userError}</PageError>;
  if (!user) return <PageError>User not found</PageError>;

  if (
    user.role_in_org !== "admin" ||
    !user.organization_id ||
    user.status !== "verified"
  ) {
    return <Navigate to="/" replace />;
  }

  return (
    <OrgSettingsContext.Provider
      value={{
        user,
        organizationDetails,
        loadingOrgDetails,
        isEnterpriseMode,
        isEmailAdminEnabled,
      }}
    >
      {children}
    </OrgSettingsContext.Provider>
  );
};

export default OrgSettingsProvider;
